// EmptyState: a single component that renders a gold-lined SVG
// illustration + eyebrow + title + supporting copy + primary CTA. Used
// on every "no data yet" surface across the app so blank tables never
// feel like a bug.
//
// Illustrations are chosen via the `illustration` prop. Pass one of the
// keys from ILLUSTRATIONS below. Every SVG is inline so nothing loads
// over the wire.
import React from "react";
import { Button } from "@/components/ui/button";

// --- Illustrations -------------------------------------------------------

function Frame({ children, aria }) {
  return (
    <svg viewBox="0 0 220 160" width="220" height="160" aria-hidden={!aria} className="drop-shadow" role={aria ? "img" : undefined}>
      <defs>
        <linearGradient id="es-gold" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e8cf9a" />
          <stop offset="1" stopColor="#7a5a1e" />
        </linearGradient>
        <linearGradient id="es-plate" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,0.06)" />
          <stop offset="1" stopColor="rgba(255,255,255,0.02)" />
        </linearGradient>
      </defs>
      {children}
    </svg>
  );
}

function KnifeAndBoard() {
  return (
    <Frame>
      <rect x="20" y="70" width="180" height="70" rx="6" fill="url(#es-plate)" stroke="url(#es-gold)" strokeWidth="1.2" />
      <line x1="30" y1="90" x2="120" y2="90" stroke="rgba(255,255,255,0.08)" />
      <line x1="30" y1="102" x2="140" y2="102" stroke="rgba(255,255,255,0.08)" />
      {/* Knife */}
      <g transform="translate(120 40) rotate(15)">
        <path d="M0 0 L80 8 L80 22 L0 22 Q-8 22 -8 11 Z" fill="url(#es-gold)" />
        <rect x="80" y="6" width="34" height="20" rx="3" fill="#1a1712" stroke="url(#es-gold)" strokeWidth="1" />
        <line x1="86" y1="12" x2="108" y2="12" stroke="rgba(232,207,154,0.7)" strokeWidth="0.6" />
      </g>
      {/* Rosemary sprig */}
      <g transform="translate(38 34)">
        <path d="M0 0 Q30 -6 60 0" stroke="rgba(232,207,154,0.6)" strokeWidth="1.4" fill="none" />
        {[0, 12, 24, 36, 48, 60].map((x) => (
          <path key={x} d={`M${x} 0 L${x - 2} -6 M${x} 0 L${x + 2} -6`} stroke="rgba(232,207,154,0.55)" strokeWidth="0.8" />
        ))}
      </g>
    </Frame>
  );
}

function EmptyPlate() {
  return (
    <Frame>
      <ellipse cx="110" cy="82" rx="78" ry="20" fill="rgba(0,0,0,0.35)" />
      <ellipse cx="110" cy="76" rx="82" ry="24" fill="url(#es-plate)" stroke="url(#es-gold)" strokeWidth="1.4" />
      <ellipse cx="110" cy="76" rx="58" ry="16" fill="none" stroke="rgba(232,207,154,0.35)" strokeWidth="0.6" />
      <ellipse cx="110" cy="76" rx="38" ry="10" fill="none" stroke="rgba(232,207,154,0.25)" strokeDasharray="3 3" strokeWidth="0.5" />
      {/* Steam */}
      {[85, 108, 132].map((x, i) => (
        <path key={i} d={`M${x} 60 Q${x + 6} 50 ${x} 40 Q${x - 6} 30 ${x} 20`} stroke="rgba(232,207,154,0.25)" strokeWidth="1" fill="none" />
      ))}
      <text x="110" y="130" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="10" letterSpacing="4" fontFamily="ui-sans-serif">CULINAIRE</text>
    </Frame>
  );
}

function WineGlass() {
  return (
    <Frame>
      <g transform="translate(80 20)">
        <path d="M0 0 Q0 -8 30 -8 Q60 -8 60 0 Q60 45 30 55 Q0 45 0 0 Z" fill="rgba(180,50,60,0.5)" stroke="url(#es-gold)" strokeWidth="1.2" />
        <ellipse cx="30" cy="0" rx="30" ry="8" fill="none" stroke="rgba(232,207,154,0.4)" strokeWidth="0.6" />
        <rect x="28" y="55" width="4" height="52" fill="url(#es-gold)" />
        <rect x="6" y="107" width="48" height="4" rx="2" fill="url(#es-gold)" />
      </g>
      <text x="110" y="150" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="10" letterSpacing="4">CELLAR</text>
    </Frame>
  );
}

function LedgerBook() {
  return (
    <Frame>
      <rect x="40" y="26" width="140" height="110" rx="6" fill="url(#es-plate)" stroke="url(#es-gold)" strokeWidth="1.2" />
      <line x1="110" y1="26" x2="110" y2="136" stroke="rgba(232,207,154,0.4)" />
      {[46, 62, 78, 94, 110].map((y) => (
        <line key={y} x1="52" y1={y} x2="102" y2={y} stroke="rgba(255,255,255,0.14)" strokeWidth="0.6" />
      ))}
      {[46, 62, 78, 94, 110].map((y) => (
        <line key={y} x1="118" y1={y} x2="168" y2={y} stroke="rgba(255,255,255,0.14)" strokeWidth="0.6" />
      ))}
      <text x="110" y="20" textAnchor="middle" fill="rgba(232,207,154,0.7)" fontSize="10" letterSpacing="3">LEDGER</text>
    </Frame>
  );
}

function SyncGears() {
  return (
    <Frame>
      {[85, 135].map((cx, i) => (
        <g key={i} transform={`translate(${cx} 80)`}>
          <circle r="26" fill="url(#es-plate)" stroke="url(#es-gold)" strokeWidth="1.2" />
          <circle r="8" fill="url(#es-gold)" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
            <rect key={angle} x="-3" y="-32" width="6" height="8" fill="url(#es-gold)" transform={`rotate(${angle})`} />
          ))}
        </g>
      ))}
      <path d="M20 30 Q60 20 100 30" stroke="rgba(232,207,154,0.5)" strokeWidth="0.8" strokeDasharray="3 3" fill="none" />
      <path d="M120 128 Q160 138 200 128" stroke="rgba(232,207,154,0.5)" strokeWidth="0.8" strokeDasharray="3 3" fill="none" />
    </Frame>
  );
}

function TeamHats() {
  return (
    <Frame>
      {[60, 110, 160].map((cx, i) => (
        <g key={i} transform={`translate(${cx} 70)`}>
          <path d="M-20 20 Q-20 -10 0 -10 Q20 -10 20 20 L20 24 Q10 20 0 22 Q-10 20 -20 24 Z" fill="#f5f2ea" stroke="rgba(0,0,0,0.15)" strokeWidth="0.5" />
          <rect x="-20" y="24" width="40" height="6" rx="1.5" fill="#e6e0d0" />
          <circle cx="0" cy="50" r="18" fill="url(#es-plate)" stroke="url(#es-gold)" strokeWidth="1" />
        </g>
      ))}
      <text x="110" y="150" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="10" letterSpacing="4">BRIGADE</text>
    </Frame>
  );
}

const ILLUSTRATIONS = {
  knife_and_board: KnifeAndBoard,
  empty_plate: EmptyPlate,
  wine_glass: WineGlass,
  ledger: LedgerBook,
  sync_gears: SyncGears,
  brigade: TeamHats,
};

// --- Component -----------------------------------------------------------

export default function EmptyState({
  illustration = "empty_plate",
  eyebrow,
  title,
  body,
  ctaLabel,
  ctaIcon: CtaIcon,
  onCtaClick,
  ctaTestId,
  secondaryLabel,
  onSecondaryClick,
  className = "",
}) {
  const Illustration = ILLUSTRATIONS[illustration] || ILLUSTRATIONS.empty_plate;
  return (
    <div
      className={`rounded-2xl glass-card p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 ${className}`}
      data-testid="empty-state"
    >
      <div className="shrink-0" data-testid={`empty-illustration-${illustration}`}>
        <Illustration />
      </div>
      <div className="flex-1 space-y-3 text-center md:text-left">
        {eyebrow && <div className="label-eyebrow gold">{eyebrow}</div>}
        <h3 className="font-display text-2xl md:text-3xl leading-tight">{title}</h3>
        {body && <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-2xl">{body}</p>}
        {(ctaLabel || secondaryLabel) && (
          <div className="pt-3 flex flex-wrap justify-center md:justify-start gap-2">
            {ctaLabel && (
              <Button onClick={onCtaClick} data-testid={ctaTestId || "empty-cta"} className="gap-1.5">
                {CtaIcon ? <CtaIcon className="w-4 h-4" /> : null}
                {ctaLabel}
              </Button>
            )}
            {secondaryLabel && (
              <Button variant="outline" onClick={onSecondaryClick} data-testid="empty-secondary">
                {secondaryLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
